import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRestaurantContext } from '@/contexts/RestaurantContext';
import { useStaff, useDeleteStaff } from '@/hooks/useStaff';
import { Header } from '@/components/layout/Header';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CreateStaffDialog } from '@/components/staff/CreateStaffDialog';
import { EditStaffDialog } from '@/components/staff/EditStaffDialog';
import { StaffLoginLink } from '@/components/staff/StaffLoginLink';
import { Trash2, UserX, UserCheck, Plus, Pencil } from 'lucide-react';
import type { Staff as StaffType, AssistantPermissions } from '@/types/entities';
import { AlertModal } from '@/components/ui/alert-modal';
import { WithPermission } from '@/components/auth/WithPermission';
import { useCurrentAssistant } from '@/hooks/useAssistantAuth';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { Badge } from '@/components/ui/badge';

export function Staff() {
  const { t, i18n } = useTranslation();
  const { selectedRestaurantId } = useRestaurantContext();
  const { data: assistant } = useCurrentAssistant();
  const { data: staff, isLoading, error, refetch } = useStaff(selectedRestaurantId);
  const deleteStaffMutation = useDeleteStaff(selectedRestaurantId || '');
  const [deletingStaffId, setDeletingStaffId] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffType | null>(null);
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
  }>({
    open: false,
    title: '',
    description: '',
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  // Helper to conditionally wrap with permission check
  const ConditionalPermission = ({
    page,
    action,
    children,
  }: {
    page: keyof AssistantPermissions;
    action: string;
    children: React.ReactNode;
  }) => {
    // If assistant is logged in, check permissions
    // If admin is logged in (no assistant), always show
    if (assistant) {
      return (
        <WithPermission page={page} action={action}>
          {children}
        </WithPermission>
      );
    }
    return <>{children}</>;
  };

  const handleDelete = async (staffId: string) => {
    setConfirmDialog({
      open: true,
      title: t('staff.deleteStaff'),
      description: t('staff.deleteStaff') + '?',
      onConfirm: async () => {
        setDeletingStaffId(staffId);
        try {
          await deleteStaffMutation.mutateAsync(staffId);
          await refetch();
        } catch (error) {
          console.error('Failed to delete staff:', error);
          const errorMessage = error instanceof Error ? error.message : t('error.generic');
          setAlertDialog({
            open: true,
            title: t('error.error'),
            description: errorMessage,
          });
        } finally {
          setDeletingStaffId(null);
        }
      },
    });
  };

  const handleStaffCreated = () => {
    refetch();
  };

  if (!selectedRestaurantId) {
    return (
      <ProtectedRoute requiredRole="admin" requiredPermission="staff">
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <Header />
          <main className="container mx-auto px-4 py-8">
            <div className="p-4 text-center text-muted-foreground">
              {t('restaurant.selectRestaurant')}
            </div>
          </main>
        </div>
      </ProtectedRoute>
    );
  }

  if (isLoading) {
    return (
      <ProtectedRoute requiredRole="admin" requiredPermission="staff">
        <LoadingSpinner fullScreen />
      </ProtectedRoute>
    );
  }

  if (error) {
    return (
      <ProtectedRoute requiredRole="admin" requiredPermission="staff">
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <Header />
          <main className="container mx-auto px-4 py-8">
            <div className="p-4 text-center text-destructive">{t('error.generic')}</div>
          </main>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute requiredRole="admin" requiredPermission="staff">
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">{t('staff.title')}</h1>
            <p className="text-muted-foreground">{t('staff.description')}</p>
          </div>

          {/* Staff Login Link */}
          {selectedRestaurantId && <StaffLoginLink restaurantId={selectedRestaurantId} />}

          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">{t('staff.title')}</h2>
                <ConditionalPermission page="staff" action="add">
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="w-4 h-4 me-2" />
                    <span className="truncate">{t('staff.createStaff')}</span>
                  </Button>
                </ConditionalPermission>
              </div>

              {!staff || staff.length === 0 ? (
                <Card>
                  <CardContent className="py-8">
                    <p className="text-center text-muted-foreground">{t('staff.noStaff')}</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {staff.map((member) => (
                    <Card key={member.id} className="flex flex-col">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg">{member.username}</CardTitle>
                            <CardDescription className="space-y-1">
                              <div>{t(`user.${member.role}`)}</div>
                              {member.specialty && (
                                <Badge
                                  variant="outline"
                                  className="mt-1"
                                  style={{
                                    borderColor: member.specialty.color,
                                    color: member.specialty.color,
                                  }}
                                >
                                  {i18n.language === 'ar'
                                    ? member.specialty.nameAr
                                    : member.specialty.nameFr}
                                </Badge>
                              )}
                              {member.role === 'server' &&
                                ((member.zones && member.zones.length > 0) || member.zone) && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {(member.zones && member.zones.length > 0
                                      ? member.zones
                                      : member.zone
                                        ? [member.zone]
                                        : []
                                    ).map((zone) => (
                                      <Badge key={zone.id} variant="outline">
                                        {i18n.language === 'ar' ? zone.nameAr : zone.nameFr}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-1 ms-2 shrink-0">
                            {member.isActive ? (
                              <UserCheck className="w-7 h-7 text-green-600" />
                            ) : (
                              <UserX className="w-7 h-7 text-red-600" />
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="flex flex-col flex-1">
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="font-medium">{t('user.active')}:</span>{' '}
                            {member.isActive ? t('staff.active') : t('staff.inactive')}
                          </div>
                        </div>

                        <div className="mt-auto pt-4 flex gap-2">
                          <ConditionalPermission page="staff" action="edit">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => setEditingStaff(member)}
                            >
                              <Pencil className="w-4 h-4 sm:me-2" />
                              <span className="hidden sm:inline">{t('common.edit')}</span>
                            </Button>
                          </ConditionalPermission>
                          <ConditionalPermission page="staff" action="delete">
                            <Button
                              variant="destructive"
                              size="sm"
                              className="flex-1"
                              onClick={() => handleDelete(member.id)}
                              disabled={deletingStaffId === member.id}
                            >
                              <Trash2 className="w-4 h-4 sm:me-2" />
                              <span className="hidden sm:inline">{t('common.delete')}</span>
                            </Button>
                          </ConditionalPermission>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>

        <CreateStaffDialog
          restaurantId={selectedRestaurantId}
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          onSuccess={handleStaffCreated}
        />

        {editingStaff && (
          <EditStaffDialog
            staff={editingStaff}
            open={!!editingStaff}
            onOpenChange={(open) => !open && setEditingStaff(null)}
            onSuccess={() => {
              // Dialog will close via onOpenChange
              // React Query will automatically refetch
            }}
          />
        )}

        <AlertModal
          open={alertDialog.open}
          onOpenChange={(open) => setAlertDialog({ ...alertDialog, open })}
          title={alertDialog.title}
          description={alertDialog.description}
        />

        <ConfirmModal
          open={confirmDialog.open}
          onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          description={confirmDialog.description}
          variant="destructive"
        />
      </div>
    </ProtectedRoute>
  );
}
